/**
 * Loume — Kopfleiste passt sich dem Hintergrund an
 * ================================================
 *
 * Logo, Menüpunkte, Symbole und der Warenkorb-Zähler sind weiß oder schwarz —
 * je nachdem, was gerade HINTER ihnen liegt. Das Glas der Leiste ändert sich
 * dabei nie: Es ist überall dasselbe dunkle (loume-header.css, Gründer
 * 27.09.2026: „der hintergrund der menü leiste soll sich nie verändern, es
 * soll liquid glass bleiben wie davor, nur die schrift und logo farbe“). Das
 * Skript setzt nur Farbklassen an den Elementen, nie etwas am Glas.
 *
 * So wird gerechnet:
 *   1. Hinter jedem Element (Logo, jeder Menüpunkt, jedes Symbol, Zähler)
 *      liegt eine Sonde. document.elementsFromPoint() liefert, was dort
 *      übereinander liegt; die Kopfleiste selbst, Drawer, Cookie-Banner und
 *      Chat zählen nicht. Was pointer-events: none trägt (Verläufe, Overlays,
 *      Videos), meldet der Browser dort nicht — das wird unter den Treffern
 *      nachgesucht und nach z-index und Reihenfolge einsortiert.
 *   2. Von oben nach unten wird gemischt: Hintergrundfarben mit Deckkraft,
 *      Verläufe an genau dieser Stelle, ::before/::after, Text anteilig,
 *      Bilder aus ihren Pixeln. Dafür lädt das Skript eine Mini-Fassung
 *      derselben Quelle (96px breit; das Shopify-CDN liefert sie mit CORS),
 *      liest sie einmal aus einem Canvas und rechnet object-fit und
 *      object-position nach. Videos: das laufende Bild, wenn es lesbar ist
 *      (crossorigin; dann alle 0,5 s neu). Sonst wird geschätzt — vor dem
 *      Start das Poster, bei kurzen Filmen ab der Hälfte das Schlussbild aus
 *      dem <template> (<lo-einmal-video>) — und der Ton braucht dort 35 %
 *      Reserve. Bei langen oder endlosen Filmen ohne lesbares Bild (Hero der
 *      Startseite) ist nichts zu schätzen: Die Elemente davor bekommen keine
 *      Klasse, es gilt der Stand ohne JavaScript. Was durchscheint, ist der
 *      Seitenhintergrund.
 *   3. Ist das Glas sichtbar (gescrollt, Maus darüber, Produktmenü offen,
 *      Produktseiten im Test „Kaufbereich unter der Kopfleiste“ —
 *      --loume-kopf-glas-an aus loume-header.css), wird es eingerechnet: der
 *      Grund weichgezeichnet wie blur(20px) (Gaußglocke, 20px
 *      Standardabweichung, über die Leistenhöhe gemittelt), darüber 10 %
 *      Schwarz aus --loume-kopf-glas. Weil das die echte Weichzeichnung nur
 *      annähert, gilt ein Ton mit Glas erst ab 10 % über dem Ziel als
 *      ausreichend.
 *   4. Für Schwarz und Weiß wird das Kontrastverhältnis nach WCAG gerechnet
 *      (Menütext Ziel 4,5 : 1, Logo, Symbole und Zähler 3 : 1). Es zählt
 *      jeweils der SCHLECHTESTE Messpunkt. Reicht ein Ton für die ganze
 *      Leiste, bekommt sie ihn ganz; sonst entscheiden links (Menü), Mitte
 *      (Logo) und rechts (Symbole) je für sich — über einem dunklen Bild
 *      links und hellem Grund rechts stimmen so beide Seiten. Umgeschaltet
 *      wird erst, wenn der andere Ton klar besser ist (Hysterese 20 %) und
 *      frühestens 0,4 s nach dem letzten Wechsel — kein Flackern an Kanten.
 *      Die Farben blenden in 0,3 s über (loume-kopf-kontrast.css).
 *
 * Gesetzt werden nur loume-kopf-el--auf-hell / --auf-dunkel an den Elementen.
 * Ohne JavaScript bleibt alles wie in loume-header.css. Bei offenem
 * Handy-Menü wird nicht gemessen: Die Leiste behält ihren Ton (Gründer,
 * 27.09.2026: sie soll sich beim Öffnen „überhaupt nicht ändern“).
 * Scheitert etwas (CORS, Canvas), fällt das Skript still auf ein neutrales
 * Grau zurück, das die Entscheidung nicht verschiebt.
 *
 * Gemessen wird rAF-gedrosselt, höchstens alle 90 ms, und nur auf Anlass:
 * Scrollen (auch in Galerien und Karussells), Größe, geladene Bilder,
 * Videos, Übergänge und Klassenwechsel in den Sections unter der Leiste,
 * Menüs; liegt ein laufendes Video oder eine CSS-Animation (Laufband)
 * darunter, zusätzlich alle 0,5 s. Dawn blendet die Leiste beim
 * Herunterscrollen aus — dann wird dort gemessen, wo sie beim Zurückscrollen
 * erscheint, damit sie gleich stimmt.
 */
(function () {
  'use strict';

  if (window.LoumeKopfKontrast) return;

  var wrapper = document.querySelector('.header-wrapper');
  if (!wrapper || !document.elementsFromPoint || !window.requestAnimationFrame) return;
  var sektion = wrapper.closest('.section-header') || wrapper.parentElement;

  // --- Einstellungen -------------------------------------------------------

  var ZIEL_TEXT = 4.5;
  var ZIEL_GRAFIK = 3;
  var HYSTERESE = 1.2;
  var MIN_VERWEIL = 400;
  var MIN_ABSTAND = 90;
  var VIDEO_TAKT = 500;
  var MINI_BREITE = 96;
  var WARTEN_MAX = 1500;
  // Glas: blur(20px) ist eine Gaußglocke mit 20px Standardabweichung.
  var GLAS_SIGMA = 20;
  var GLAS_SCHRITT = 8;
  var SONDEN_RASTER = 48;
  // Das Glasmodell (Mittel statt echter Weichzeichnung) ist grob: ein Ton
  // gilt mit Glas erst ab 10 % über dem Ziel als ausreichend.
  var GLAS_RESERVE = 1.1;
  var TEXT_DECKUNG = 0.25;
  // Längere oder endlose Filme ohne lesbares Bild: nicht zu schätzen.
  var KURZFILM = 15;
  // Geschätzter Grund (Video ohne lesbares Bild, Bild ohne Mini-Fassung):
  // dort muss ein Ton sein Ziel um 35 % übertreffen, um als ausreichend zu gelten.
  var UNSICHER = 1.35;
  // Unbekannter, deckender Inhalt (fremdes Bild, iframe): das Grau, auf dem
  // Schwarz und Weiß gleich gut sind — es verschiebt die Entscheidung nicht.
  var NEUTRAL = [118, 118, 118, 1];
  // Film ohne lesbares Bild hinter dem Handy-Menü: die Filme im Shop sind
  // Nachtszenen, deshalb dort als dunkel gewertet (nur für das Menü).
  var DUNKEL_FILM = [40, 40, 40, 1];

  var IGNORIEREN = [
    '.section-header', 'sticky-header',
    'cart-drawer', 'cart-notification', 'menu-drawer', '.menu-drawer',
    'details-modal', '.search-modal', 'quick-add-modal',
    '#shopify-pc__banner', '#shopify-pc__prefs', '.shopify-pc__banner',
    '#preview-bar-iframe', '.shopify-preview-bar', '#shopify-chat', 'inbox-online-store-chat',
    '[data-loume-kopf-ignorieren]'
  ].join(',');
  try {
    document.documentElement.closest(IGNORIEREN + ',[id*="superchat" i],[class*="superchat" i]');
    IGNORIEREN += ',[id*="superchat" i],[class*="superchat" i]';
  } catch (e) {
    IGNORIEREN += ',[id*="superchat"],[class*="superchat"]';
  }

  var KL_EL = { hell: 'loume-kopf-el--auf-hell', dunkel: 'loume-kopf-el--auf-dunkel' };
  var ANDERS = { hell: 'dunkel', dunkel: 'hell' };

  // --- Farben und Kontrast -------------------------------------------------

  var LIN = new Float32Array(256);
  for (var i = 0; i < 256; i++) {
    var c = i / 255;
    LIN[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  function kanal(v) {
    return v <= 0 ? 0 : v >= 255 ? 255 : Math.round(v);
  }
  function lum(f) {
    return 0.2126 * LIN[kanal(f[0])] + 0.7152 * LIN[kanal(f[1])] + 0.0722 * LIN[kanal(f[2])];
  }
  function kontrast(a, b) {
    var la = lum(a);
    var lb = lum(b);
    return la > lb ? (la + 0.05) / (lb + 0.05) : (lb + 0.05) / (la + 0.05);
  }
  // Farbe mit Alpha über deckenden Grund.
  function ueber(oben, unten) {
    var a = oben[3];
    return [oben[0] * a + unten[0] * (1 - a), oben[1] * a + unten[1] * (1 - a), oben[2] * a + unten[2] * (1 - a), 1];
  }
  function tinteKontrast(tinte, grund) {
    return kontrast(tinte[3] < 1 ? ueber(tinte, grund) : tinte, grund);
  }

  function kontext(w, h) {
    var cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    return cv.getContext('2d', { willReadFrequently: true });
  }

  var farbCache = new Map();
  var farbCtx = null;
  var RGB = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,\/]+([\d.]+)(%?))?\s*\)$/;
  function farbe(str) {
    if (!str || str === 'transparent' || str === 'none') return [0, 0, 0, 0];
    var f = farbCache.get(str);
    if (f) return f;
    var m = RGB.exec(str.trim());
    if (m) {
      f = [+m[1], +m[2], +m[3], m[4] === undefined ? 1 : parseFloat(m[4]) / (m[5] ? 100 : 1)];
    } else {
      // Andere Schreibweisen (#hex, color(), oklab() aus color-mix …):
      // einen Pixel malen und zurücklesen.
      if (!farbCtx) farbCtx = kontext(1, 1);
      farbCtx.clearRect(0, 0, 1, 1);
      farbCtx.fillStyle = 'rgba(0,0,0,0)';
      farbCtx.fillStyle = str.trim();
      farbCtx.fillRect(0, 0, 1, 1);
      var d = farbCtx.getImageData(0, 0, 1, 1).data;
      f = [d[0], d[1], d[2], d[3] / 255];
    }
    farbCache.set(str, f);
    return f;
  }

  // Farben der Leiste kommen aus loume-kopf-kontrast.css, das Glas aus
  // loume-header.css (je eine Quelle).
  var FARBEN = {};
  function farbenLesen() {
    var cs = getComputedStyle(wrapper);
    function v(name, ersatz) {
      var w = cs.getPropertyValue(name).trim();
      return farbe(w || ersatz);
    }
    FARBEN.tinte = v('--loume-kopf-tinte', '#121212');
    FARBEN.weiss = v('--loume-kopf-weiss', '#ffffff');
    FARBEN.weissLeise = v('--loume-kopf-weiss-leise', 'rgba(255, 255, 255, 0.75)');
    // Das eine Glas der Leiste (loume-header.css).
    FARBEN.glas = v('--loume-kopf-glas', 'rgba(0, 0, 0, 0.1)');
    // Ist es im Zielzustand sichtbar? Eine Variable statt der Glasfarbe
    // selbst, weil die beim Einblenden 0,9 s lang überblendet.
    FARBEN.glasAn = cs.getPropertyValue('--loume-kopf-glas-an').trim() === '1';
  }

  // --- Texte zerlegen (Kommalisten, Verläufe) ------------------------------

  function teilen(s) {
    var teile = [];
    var tiefe = 0;
    var start = 0;
    for (var k = 0; k < s.length; k++) {
      var z = s.charCodeAt(k);
      if (z === 40) tiefe++;
      else if (z === 41) tiefe--;
      else if (z === 44 && tiefe === 0) {
        teile.push(s.slice(start, k).trim());
        start = k + 1;
      }
    }
    teile.push(s.slice(start).trim());
    return teile;
  }

  function lage(token, frei) {
    if (!token) return frei / 2;
    if (token === 'left' || token === 'top') return 0;
    if (token === 'right' || token === 'bottom') return frei;
    if (token === 'center') return frei / 2;
    var n = parseFloat(token);
    if (isNaN(n)) return frei / 2;
    return token.indexOf('%') > -1 ? (frei * n) / 100 : n;
  }

  var verlaufCache = new Map();
  function verlauf(str) {
    if (verlaufCache.has(str)) return verlaufCache.get(str);
    var v = null;
    var m = /^(repeating-)?(linear|radial|conic)-gradient\((.*)\)$/.exec(str);
    if (m) {
      var teile = teilen(m[3]);
      v = { art: m[2], wiederholt: !!m[1], winkel: 180, ecke: null, stopps: [] };
      var erstes = teile[0];
      var ab = 0;
      if (v.art === 'linear') {
        if (/^-?[\d.]+(deg|grad|rad|turn)$/.test(erstes)) {
          var n = parseFloat(erstes);
          v.winkel = /turn$/.test(erstes) ? n * 360 : /grad$/.test(erstes) ? n * 0.9 : /rad$/.test(erstes) ? (n * 180) / Math.PI : n;
          ab = 1;
        } else if (/^to /.test(erstes)) {
          var w = erstes.slice(3);
          var sx = /right/.test(w) ? 1 : /left/.test(w) ? -1 : 0;
          var sy = /bottom/.test(w) ? 1 : /top/.test(w) ? -1 : 0;
          if (sx && sy) v.ecke = [sx, sy];
          else v.winkel = sx === 1 ? 90 : sx === -1 ? 270 : sy === -1 ? 0 : 180;
          ab = 1;
        }
      } else if (!/^(rgb|hsl|#|color|oklab|oklch|lab|lch|transparent|[a-z]+\s+[\d.]+%?$)/.test(erstes) || /^(circle|ellipse|closest|farthest|at |from )/.test(erstes)) {
        ab = 1;
      }
      for (var k = ab; k < teile.length; k++) {
        var t = teile[k];
        var fm = /^((?:[a-z-]+\([^)]*\))|#[0-9a-f]+|[a-z]+)\s*(.*)$/i.exec(t);
        if (!fm) continue; // Farbhinweis ohne Farbe
        var pos = fm[2] ? fm[2].split(/\s+/)[0] : '';
        v.stopps.push({ f: farbe(fm[1]), pos: pos ? parseFloat(pos) : null, px: /px$/.test(pos) });
      }
      if (!v.stopps.length) v = null;
    }
    verlaufCache.set(str, v);
    return v;
  }

  function verlaufFarbe(v, box, x, y) {
    var t;
    var laenge;
    var st = v.stopps;
    if (v.art === 'conic') {
      // Grob: Mittel aller Stopps.
      var r = 0, g = 0, b = 0, a = 0;
      for (var q = 0; q < st.length; q++) {
        r += st[q].f[0] * st[q].f[3];
        g += st[q].f[1] * st[q].f[3];
        b += st[q].f[2] * st[q].f[3];
        a += st[q].f[3];
      }
      return a > 0 ? [r / a, g / a, b / a, a / st.length] : [0, 0, 0, 0];
    }
    var cx = box.left + box.width / 2;
    var cy = box.top + box.height / 2;
    if (v.art === 'linear') {
      var winkel = v.winkel;
      if (v.ecke) winkel = (Math.atan2(v.ecke[0] * box.height, -v.ecke[1] * box.width) * 180) / Math.PI;
      var rad = (winkel * Math.PI) / 180;
      var s = Math.sin(rad);
      var c2 = Math.cos(rad);
      laenge = Math.abs(box.width * s) + Math.abs(box.height * c2) || 1;
      t = ((x - cx) * s - (y - cy) * c2) / laenge + 0.5;
    } else {
      var dx = (x - cx) / (box.width / 2 || 1);
      var dy = (y - cy) / (box.height / 2 || 1);
      laenge = Math.hypot(box.width, box.height) / 2 || 1;
      t = Math.sqrt(dx * dx + dy * dy) / Math.SQRT2;
    }
    // Stopps auflösen: fehlende Lagen gleichmäßig verteilen.
    var p = [];
    for (var k = 0; k < st.length; k++) {
      var wert = st[k].pos;
      p.push(wert === null ? null : st[k].px ? wert / laenge : wert / 100);
    }
    if (p[0] === null) p[0] = 0;
    if (p[p.length - 1] === null) p[p.length - 1] = Math.max(1, p[0]);
    for (var j = 1; j < p.length; j++) {
      if (p[j] !== null) {
        if (p[j] < p[j - 1]) p[j] = p[j - 1];
        continue;
      }
      var e = j;
      while (p[e] === null) e++;
      for (var u = j; u < e; u++) p[u] = p[j - 1] + ((p[e] - p[j - 1]) * (u - j + 1)) / (e - j + 1);
    }
    if (v.wiederholt) {
      var spanne = p[p.length - 1] - p[0];
      if (spanne > 0) t = p[0] + ((((t - p[0]) % spanne) + spanne) % spanne);
    }
    if (t <= p[0]) return st[0].f;
    if (t >= p[p.length - 1]) return st[st.length - 1].f;
    for (var h = 1; h < p.length; h++) {
      if (t <= p[h]) {
        var anteil = p[h] > p[h - 1] ? (t - p[h - 1]) / (p[h] - p[h - 1]) : 1;
        var f0 = st[h - 1].f;
        var f1 = st[h].f;
        // Wie der Browser: vormultipliziert mischen.
        var a0 = f0[3];
        var a1 = f1[3];
        var am = a0 + (a1 - a0) * anteil;
        if (am <= 0) return [0, 0, 0, 0];
        return [
          (f0[0] * a0 + (f1[0] * a1 - f0[0] * a0) * anteil) / am,
          (f0[1] * a0 + (f1[1] * a1 - f0[1] * a0) * anteil) / am,
          (f0[2] * a0 + (f1[2] * a1 - f0[2] * a0) * anteil) / am,
          am
        ];
      }
    }
    return st[st.length - 1].f;
  }

  // --- Mini-Fassungen von Bildern ------------------------------------------

  var FEHLT = { status: 'fehler' };
  var UNBEKANNT = { status: 'fehler', unbekannt: true };
  var minis = new Map();

  function miniAdresse(src, klein) {
    var u;
    try {
      u = new URL(src, location.href);
    } catch (e) {
      return null;
    }
    if (u.protocol === 'data:' || u.protocol === 'blob:') return src;
    var shopify = u.hostname === 'cdn.shopify.com' || u.pathname.indexOf('/cdn/shop/') === 0 || u.pathname.indexOf('/s/files/') === 0;
    if (shopify) {
      if (/\.svg$/i.test(u.pathname)) return u.href;
      var w = parseFloat(u.searchParams.get('width'));
      var h = parseFloat(u.searchParams.get('height'));
      if (w && h) u.searchParams.set('height', String(Math.max(1, Math.round((h * MINI_BREITE) / w))));
      else u.searchParams.delete('height');
      u.searchParams.set('width', String(MINI_BREITE));
      return u.href;
    }
    // Fremde Quellen nur, wenn sie ohnehin klein sind (App-Symbole) oder vom
    // eigenen Host kommen (dann liegt die Datei schon im Cache).
    if (klein || u.origin === location.origin) return u.href;
    return null;
  }

  function mini(src, klein) {
    var adresse = src && miniAdresse(src, klein);
    if (!adresse) return FEHLT;
    var m = minis.get(adresse);
    if (m) return m;
    m = { status: 'laedt', seit: performance.now(), w: 0, h: 0, daten: null };
    minis.set(adresse, m);
    var bild = new Image();
    bild.crossOrigin = 'anonymous';
    bild.decoding = 'async';
    if ('fetchPriority' in bild) bild.fetchPriority = 'low';
    bild.onload = function () {
      try {
        var nw = bild.naturalWidth || MINI_BREITE;
        var nh = bild.naturalHeight || MINI_BREITE;
        var w = Math.max(1, Math.min(MINI_BREITE, nw));
        var h = Math.max(1, Math.min(4 * MINI_BREITE, Math.round((w * nh) / nw)));
        var ctx = kontext(w, h);
        ctx.drawImage(bild, 0, 0, w, h);
        m.daten = ctx.getImageData(0, 0, w, h).data;
        m.w = w;
        m.h = h;
        m.status = 'fertig';
      } catch (e) {
        m.status = 'fehler';
      }
      planen();
    };
    bild.onerror = function () {
      m.status = 'fehler';
      planen();
    };
    bild.src = adresse;
    return m;
  }

  // Videobilder: nur lesbar, wenn das Video mit CORS geladen wurde (oder vom
  // eigenen Host kommt). Sonst gilt das Poster.
  var videoBilder = new WeakMap();
  function videoBild(video) {
    var vb = videoBilder.get(video);
    if (!vb) {
      vb = { gesperrt: false, zeit: -1, stand: -1, w: 0, h: 0, daten: null, ctx: null };
      videoBilder.set(video, vb);
    }
    if (vb.gesperrt || video.readyState < 2) return null;
    var quelle = video.currentSrc || video.src;
    if (!video.crossOrigin && quelle) {
      try {
        if (new URL(quelle, location.href).origin !== location.origin) {
          vb.gesperrt = true;
          return null;
        }
      } catch (e) {
        vb.gesperrt = true;
        return null;
      }
    }
    var jetzt = performance.now();
    if (vb.daten && (vb.stand === video.currentTime || jetzt - vb.zeit < VIDEO_TAKT - 20)) return vb;
    try {
      var vw = video.videoWidth || 16;
      var vh = video.videoHeight || 9;
      var w = Math.min(64, vw);
      var h = Math.max(1, Math.round((w * vh) / vw));
      if (!vb.ctx || vb.w !== w || vb.h !== h) vb.ctx = kontext(w, h);
      vb.ctx.drawImage(video, 0, 0, w, h);
      vb.daten = vb.ctx.getImageData(0, 0, w, h).data;
      vb.w = w;
      vb.h = h;
      vb.zeit = jetzt;
      vb.stand = video.currentTime;
      return vb;
    } catch (e) {
      vb.gesperrt = true;
      return null;
    }
  }

  // Nicht lesbares Video: was zeigt es gerade? Vor dem Start (oder an den
  // Anfang zurückgesetzt) das Poster — das ist genau; ohne Poster null: dann
  // ist das Video durchsichtig, und es zählt das Standbild darunter (bei
  // <lo-einmal-video> das erste Bild des Films). Läuft ein kurzer Film (bis
  // 15 s, ohne Schleife) oder steht er am Ende, bleibt eine Schätzung: vom
  // Anfangsbild zum Schlussbild, das <lo-einmal-video> im <template>
  // bereithält, nach Fortschritt überblendet ({ start, ende, anteil }). Bei
  // langen oder endlosen Filmen (Hero der Startseite: 56 s, Schleife) sagt
  // das Poster nichts über das laufende Bild — dann gilt der Grund als
  // unbekannt, und die Elemente davor fallen still auf den Stand ohne
  // JavaScript zurück (loume-header.css).
  var schlussbilder = new WeakMap();
  function schlussbildAdresse(video) {
    if (schlussbilder.has(video)) return schlussbilder.get(video);
    var adresse = null;
    var vorlage = video.parentElement && video.parentElement.querySelector('template');
    if (vorlage && vorlage.content) {
      var quellen = vorlage.content.querySelectorAll('source[media]');
      for (var k = 0; k < quellen.length && !adresse; k++) {
        if (window.matchMedia(quellen[k].media).matches) adresse = (quellen[k].getAttribute('srcset') || '').trim().split(/\s+/)[0];
      }
      var bild = vorlage.content.querySelector('img');
      if (!adresse && bild) adresse = bild.getAttribute('src') || (bild.getAttribute('srcset') || '').trim().split(/\s+/)[0];
    }
    schlussbilder.set(video, adresse || null);
    return adresse || null;
  }
  function videoSchaetzung(op) {
    var v = op.el;
    if (v.readyState < 2) return op.poster;
    if (v.paused && !v.ended && v.currentTime < 0.05) return op.poster;
    if (v.loop || !(v.duration > 0) || v.duration > KURZFILM) return UNBEKANNT;
    var schluss = schlussbildAdresse(v);
    if (!schluss) {
      unsicher = true;
      return op.poster;
    }
    // Am Ende steht genau das Schlussbild — das ist keine Schätzung mehr.
    if (!v.ended) unsicher = true;
    return { start: op.poster, ende: mini(schluss, false), anteil: v.ended ? 1 : Math.min(1, v.currentTime / v.duration) };
  }

  function pixel(m, u, v) {
    var x = Math.min(m.w - 1, Math.max(0, Math.floor(u * m.w)));
    var y = Math.min(m.h - 1, Math.max(0, Math.floor(v * m.h)));
    var k = (y * m.w + x) * 4;
    var d = m.daten;
    return [d[k], d[k + 1], d[k + 2], d[k + 3] / 255];
  }

  // Wo liegt ein Bild mit object-fit/object-position in seinem Kasten?
  // Gerechnet im Layout-Maß, dann auf das (evtl. transformierte) Rechteck.
  function passung(el, cs, box, iw, ih) {
    var W = el.offsetWidth || box.width;
    var H = el.offsetHeight || box.height;
    var fit = cs.objectFit || 'fill';
    var ar = iw / ih;
    var rw;
    var rh;
    var natW = el.naturalWidth || el.videoWidth || 0;
    var natH = el.naturalHeight || el.videoHeight || 0;
    if (fit === 'fill') {
      rw = W;
      rh = H;
    } else if (fit === 'none') {
      rw = natW || W;
      rh = natH || H;
    } else if ((fit === 'contain' || fit === 'scale-down') === W / H > ar) {
      // contain bei breitem Kasten, cover bei hohem: Höhe füllt
      rh = H;
      rw = H * ar;
    } else {
      rw = W;
      rh = W / ar;
    }
    if (fit === 'scale-down' && natW && rw > natW) {
      rw = natW;
      rh = natH;
    }
    var teile = (cs.objectPosition || '50% 50%').split(/\s+/);
    var sx = box.width / (W || 1);
    var sy = box.height / (H || 1);
    return {
      left: box.left + lage(teile[0], W - rw) * sx,
      top: box.top + lage(teile[1], H - rh) * sy,
      width: rw * sx,
      height: rh * sy
    };
  }

  // Dasselbe für ein Hintergrundbild (background-size/-position).
  function bgPassung(groesse, position, box, iw, ih) {
    var W = box.width;
    var H = box.height;
    var ar = iw / ih;
    var rw;
    var rh;
    var g = (groesse || 'auto').split(/\s+/);
    if (g[0] === 'cover' || g[0] === 'contain') {
      if ((g[0] === 'contain') === W / H > ar) {
        rh = H;
        rw = H * ar;
      } else {
        rw = W;
        rh = W / ar;
      }
    } else {
      var gw = g[0];
      var gh = g[1] || 'auto';
      rw = gw === 'auto' ? null : gw.indexOf('%') > -1 ? (W * parseFloat(gw)) / 100 : parseFloat(gw);
      rh = gh === 'auto' ? null : gh.indexOf('%') > -1 ? (H * parseFloat(gh)) / 100 : parseFloat(gh);
      if (rw === null && rh === null) {
        // Natürliche Größe unbekannt (Mini-Fassung): wie cover behandeln.
        if (W / H > ar) {
          rw = W;
          rh = W / ar;
        } else {
          rh = H;
          rw = H * ar;
        }
      } else if (rw === null) rw = rh * ar;
      else if (rh === null) rh = rw / ar;
    }
    var p = (position || '50% 50%').split(/\s+/);
    return { left: box.left + lage(p[0], W - rw), top: box.top + lage(p[1], H - rh), width: rw, height: rh };
  }

  // --- Ein Messdurchgang ---------------------------------------------------

  var durchgang = null; // Merker, gilt nur für einen Durchgang
  var WARTET = {};
  var unsicher = false; // der zuletzt gemischte Punkt beruht auf einer Schätzung
  var unbekannt = false; // … oder auf einem Film, dessen Bild nicht zu schätzen ist

  function neuerDurchgang() {
    durchgang = {
      stil: new Map(),
      rechteck: new Map(),
      deckung: new Map(),
      ignoriert: new Map(),
      ebenen: new Map(),
      stapel: new Map(),
      grund: new Map(),
      videos: [],
      bewegt: false,
      geprueft: new Set(),
      wartet: false,
      sektionen: new Set(),
      sonden: 0
    };
  }

  function stil(el) {
    var s = durchgang.stil.get(el);
    if (!s) {
      s = getComputedStyle(el);
      durchgang.stil.set(el, s);
    }
    return s;
  }
  function rechteck(el) {
    var r = durchgang.rechteck.get(el);
    if (!r) {
      r = el.getBoundingClientRect();
      durchgang.rechteck.set(el, r);
    }
    return r;
  }
  function deckung(el) {
    if (!el || el.nodeType !== 1) return 1;
    var d = durchgang.deckung.get(el);
    if (d === undefined) {
      var cs = stil(el);
      d = cs.visibility === 'hidden' ? 0 : parseFloat(cs.opacity);
      if (isNaN(d)) d = 1;
      if (d > 0 && el.parentElement) d *= deckung(el.parentElement);
      durchgang.deckung.set(el, d);
    }
    return d;
  }
  function ignoriert(el) {
    var n = durchgang.ignoriert.get(el);
    if (n === undefined) {
      n = !!el.closest(IGNORIEREN);
      durchgang.ignoriert.set(el, n);
    }
    return n;
  }
  function drin(b, x, y) {
    return x >= b.left && x < b.left + b.width && y >= b.top && y < b.top + b.height;
  }

  // Hintergrund eines Elements oder Pseudo-Elements als Ebenen (oberste zuerst).
  function hintergrund(cs, d, box, liste) {
    if ((cs.backgroundClip || '').indexOf('text') > -1) return;
    var bilder = cs.backgroundImage;
    if (bilder && bilder !== 'none') {
      var teile = teilen(bilder);
      var groessen = teilen(cs.backgroundSize || 'auto');
      var lagen = teilen(cs.backgroundPosition || '0% 0%');
      var wdh = teilen(cs.backgroundRepeat || 'repeat');
      var fest = teilen(cs.backgroundAttachment || 'scroll');
      for (var k = 0; k < teile.length; k++) {
        var t = teile[k];
        var kasten = fest[k % fest.length] === 'fixed' ? { left: 0, top: 0, width: innerWidth, height: innerHeight } : box;
        if (t.indexOf('url(') === 0) {
          var url = t.slice(4, -1).replace(/^["']|["']$/g, '');
          liste.push({
            art: 'bgbild',
            m: mini(url, false),
            groesse: groessen[k % groessen.length],
            lage: lagen[k % lagen.length],
            wiederholt: wdh[k % wdh.length].indexOf('no-repeat') < 0,
            box: kasten,
            d: d
          });
        } else if (t.indexOf('gradient(') > -1) {
          var v = verlauf(t);
          if (v) liste.push({ art: 'verlauf', v: v, box: kasten, d: d });
        }
      }
    }
    var f = farbe(cs.backgroundColor);
    if (f[3] > 0) liste.push({ art: 'farbe', f: f, box: box, d: d });
  }

  function pseudo(el, cs, welches, box, d, liste) {
    var ps = getComputedStyle(el, welches);
    var inhalt = ps.content;
    if (!inhalt || inhalt === 'none' || inhalt === 'normal' || ps.display === 'none' || ps.visibility === 'hidden') return;
    var bi = ps.backgroundImage;
    if (farbe(ps.backgroundColor)[3] === 0 && (!bi || bi === 'none')) return;
    var pd = d * (parseFloat(ps.opacity) || 0);
    if (pd < 0.02) return;
    var pos = ps.position;
    if (pos !== 'absolute' && pos !== 'fixed') return;
    if (pos === 'absolute' && cs.position === 'static') return;
    var l = parseFloat(ps.left);
    var t = parseFloat(ps.top);
    var w = parseFloat(ps.width);
    var h = parseFloat(ps.height);
    if (isNaN(l) || isNaN(t) || isNaN(w) || isNaN(h)) return;
    var basis = pos === 'fixed' ? { left: 0, top: 0 } : box;
    hintergrund(ps, pd, { left: basis.left + l, top: basis.top + t, width: w, height: h }, liste);
  }

  // Ebenen eines Elements: haupt (Inhalt, ::before, Hintergrund) und nach
  // (::after — liegt über den Kindern).
  function ebenen(el) {
    var e = durchgang.ebenen.get(el);
    if (e) return e;
    e = { haupt: [], nach: [] };
    durchgang.ebenen.set(el, e);
    var d = deckung(el);
    if (d < 0.02) return e;
    var cs = stil(el);
    var box = rechteck(el);
    var tag = el.tagName;
    if (tag === 'IMG') {
      if (el.complete && el.naturalWidth > 0) {
        var src = el.currentSrc || el.src;
        e.haupt.push({ art: 'bild', el: el, cs: cs, m: mini(src, el.naturalWidth * el.naturalHeight <= 160000), box: box, d: d });
      }
    } else if (tag === 'VIDEO') {
      durchgang.videos.push(el);
      if (!el.paused && !el.ended) durchgang.bewegt = true;
      e.haupt.push({ art: 'video', el: el, cs: cs, box: box, d: d, poster: el.poster ? mini(el.poster, false) : null });
    } else if (tag === 'IFRAME' || tag === 'EMBED' || tag === 'OBJECT') {
      e.haupt.push({ art: 'farbe', f: NEUTRAL, box: box, d: d });
    } else if (tag !== 'CANVAS' && tag !== 'HTML' && tag !== 'BODY') {
      // Text anteilig: Glyphen decken nur einen Teil der Zeile.
      var hatText = false;
      for (var n = el.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3 && /\S/.test(n.data)) {
          hatText = true;
          break;
        }
      }
      if (hatText) {
        var tf = farbe(cs.color);
        if (tf[3] > 0) {
          var bereich = document.createRange();
          var zeilen = [];
          for (var n2 = el.firstChild; n2; n2 = n2.nextSibling) {
            if (n2.nodeType !== 3 || !/\S/.test(n2.data)) continue;
            bereich.selectNodeContents(n2);
            var rs = bereich.getClientRects();
            for (var q = 0; q < rs.length; q++) zeilen.push(rs[q]);
          }
          if (zeilen.length) e.haupt.push({ art: 'text', f: [tf[0], tf[1], tf[2], tf[3] * TEXT_DECKUNG], zeilen: zeilen, d: d });
        }
      }
      pseudo(el, cs, '::after', box, d, e.nach);
      pseudo(el, cs, '::before', box, d, e.haupt);
    }
    hintergrund(cs, d, box, e.haupt);
    return e;
  }

  // Pixel eines Bildes (Mini-Fassung) an (x, y), mit object-fit/-position
  // bzw. background-size/-position: [r, g, b, a], null (außerhalb) oder WARTET.
  function bildPixel(op, quelle, x, y) {
    if (quelle.status === 'laedt') {
      if (performance.now() - quelle.seit < WARTEN_MAX) return WARTET;
      unsicher = true;
      return NEUTRAL;
    }
    if (quelle.status === 'fehler') {
      unsicher = true;
      if (quelle.unbekannt) unbekannt = true;
      if (quelle.unbekannt && menueModus) return DUNKEL_FILM;
      return NEUTRAL;
    }
    // Die Lage des Bildes im Kasten je Quelle nur einmal rechnen
    // (object-fit, offsetWidth — sonst der teuerste Teil).
    var lagebox = op.lagen && op.lagen.get(quelle);
    if (!lagebox) {
      lagebox = op.art === 'bgbild' ? bgPassung(op.groesse, op.lage, op.box, quelle.w, quelle.h) : passung(op.el, op.cs, op.box, quelle.w, quelle.h);
      if (!op.lagen) op.lagen = new Map();
      op.lagen.set(quelle, lagebox);
    }
    var u = (x - lagebox.left) / lagebox.width;
    var v = (y - lagebox.top) / lagebox.height;
    if (op.art === 'bgbild' && op.wiederholt) {
      u -= Math.floor(u);
      v -= Math.floor(v);
    }
    if (u < 0 || u >= 1 || v < 0 || v >= 1) return null;
    return pixel(quelle, u, v);
  }

  // Farbe einer Ebene an (x, y): [r, g, b, a], null (durchsichtig) oder WARTET.
  function ebenenFarbe(op, x, y) {
    var f;
    switch (op.art) {
      case 'farbe':
        if (!drin(op.box, x, y)) return null;
        f = op.f;
        break;
      case 'verlauf':
        if (!drin(op.box, x, y)) return null;
        f = verlaufFarbe(op.v, op.box, x, y);
        break;
      case 'text':
        f = null;
        for (var k = 0; k < op.zeilen.length; k++) {
          var z = op.zeilen[k];
          if (x >= z.left && x < z.right && y >= z.top && y < z.bottom) {
            f = op.f;
            break;
          }
        }
        if (!f) return null;
        break;
      case 'bild':
      case 'bgbild':
        if (!drin(op.box, x, y)) return null;
        f = bildPixel(op, op.m, x, y);
        if (!f || f === WARTET) return f;
        break;
      case 'video': {
        if (!drin(op.box, x, y)) return null;
        var lesbar = videoBild(op.el);
        if (lesbar) {
          f = bildPixel(op, lesbar, x, y);
          if (!f || f === WARTET) return f;
          break;
        }
        var sch = videoSchaetzung(op);
        // Glasblatt: hinter einem langen Film zählt sein Poster (geschätzt).
        if (sch === UNBEKANNT && blattModus && op.poster) {
          unsicher = true;
          sch = op.poster;
        }
        if (!sch) return null; // kein Poster: durchsichtig, das Standbild darunter zählt
        if (!sch.ende) {
          f = bildPixel(op, sch, x, y);
          if (!f || f === WARTET) return f;
          break;
        }
        // Laufender kurzer Film: vom Anfangs- zum Schlussbild überblenden,
        // nach Fortschritt. Ohne Poster liegt das Schlussbild mit dieser
        // Deckkraft über dem Standbild darunter.
        var fe = bildPixel(op, sch.ende, x, y);
        if (fe === WARTET) return WARTET;
        var fa = sch.start ? bildPixel(op, sch.start, x, y) : null;
        if (fa === WARTET) return WARTET;
        if (!fe) f = fa;
        else if (!fa) f = [fe[0], fe[1], fe[2], fe[3] * sch.anteil];
        else f = [fa[0] + (fe[0] - fa[0]) * sch.anteil, fa[1] + (fe[1] - fa[1]) * sch.anteil, fa[2] + (fe[2] - fa[2]) * sch.anteil, 1];
        if (!f) return null;
        break;
      }
      default:
        return null;
    }
    return op.d < 1 ? [f[0], f[1], f[2], f[3] * op.d] : f;
  }

  // Liegt a (nicht im Trefferstapel) über b? Grob nach CSS-Malreihenfolge:
  // z-index < 0, dann statische Blöcke, dann positionierte in Reihenfolge,
  // dann z-index > 0.
  function rang(el) {
    var cs = stil(el);
    var z = parseInt(cs.zIndex, 10);
    if (cs.position === 'static') {
      // z-index wirkt ohne Positionierung nur bei Flex- und Grid-Kindern.
      var pd = el.parentElement ? stil(el.parentElement).display : '';
      if (!isNaN(z) && pd.indexOf('flex') < 0 && pd.indexOf('grid') < 0) z = NaN;
      if (isNaN(z)) return parseFloat(cs.opacity) < 1 || cs.transform !== 'none' ? 1 : 0;
    }
    if (isNaN(z) || z === 0) return 1;
    return z < 0 ? z - 1 : 1 + z;
  }
  function liegtUeber(a, b) {
    var kette = [];
    for (var x = a; x; x = x.parentElement) kette.push(x);
    var vb = b;
    var vorB = null;
    while (vb && kette.indexOf(vb) < 0) {
      vorB = vb;
      vb = vb.parentElement;
    }
    if (!vb || !vorB) return false;
    var vorA = kette[kette.indexOf(vb) - 1];
    if (!vorA) return false;
    var ra = rang(vorA);
    var rb = rang(vorB);
    if (ra !== rb) return ra > rb;
    return !(vorA.compareDocumentPosition(vorB) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  function einsortieren(liste, neu) {
    var vorfahr = neu.parentElement;
    while (vorfahr && liste.indexOf(vorfahr) < 0) vorfahr = vorfahr.parentElement;
    var iVorfahr = vorfahr ? liste.indexOf(vorfahr) : liste.length;
    var ziel = iVorfahr;
    var mindestens = 0;
    for (var j = 0; j < iVorfahr; j++) {
      var s = liste[j];
      if (neu.contains(s)) {
        mindestens = j + 1;
        continue;
      }
      if (ziel === iVorfahr && vorfahr && vorfahr.contains(s) && liegtUeber(neu, s)) ziel = j;
    }
    liste.splice(Math.max(ziel, mindestens), 0, neu);
  }

  function nachsuchen(liste, x, y) {
    var imStapel = new Set(liste);
    var zusatz = [];
    function suchen(eltern, tiefe) {
      var kinder = eltern.children;
      if (!kinder.length || kinder.length > 40) return;
      for (var k = 0; k < kinder.length; k++) {
        var kind = kinder[k];
        if (imStapel.has(kind) || !(kind instanceof HTMLElement)) continue;
        var r = rechteck(kind);
        if (x < r.left || x >= r.right || y < r.top || y >= r.bottom) continue;
        if (ignoriert(kind) || deckung(kind) < 0.02) continue;
        imStapel.add(kind);
        zusatz.push(kind);
        if (tiefe < 2) suchen(kind, tiefe + 1);
      }
    }
    for (var k = 0; k < liste.length; k++) {
      var el = liste[k];
      if (el === document.documentElement || el === document.body || el.id === 'MainContent') continue;
      suchen(el, 0);
    }
    for (var z = 0; z < zusatz.length; z++) einsortieren(liste, zusatz[z]);
  }

  // Alle Ebenen an einer Sonde, oberste zuerst.
  function stapel(x, y) {
    var schluessel = Math.round(x) + ',' + Math.round(y);
    var ops = durchgang.stapel.get(schluessel);
    if (ops) return ops;
    durchgang.sonden++;
    var roh = document.elementsFromPoint(x, y);
    var liste = [];
    for (var k = 0; k < roh.length; k++) {
      var el = roh[k];
      if (!(el instanceof HTMLElement) && el !== document.documentElement) continue;
      if (ignoriert(el)) continue;
      liste.push(el);
      var sek = el.closest('.shopify-section');
      if (sek) durchgang.sektionen.add(sek);
      // Laufbänder und andere CSS-Animationen bewegen den Inhalt ohne
      // Ereignis — dann in Abständen nachmessen.
      if (!durchgang.bewegt && el.getAnimations && !durchgang.geprueft.has(el) && el !== document.body && el !== document.documentElement) {
        durchgang.geprueft.add(el);
        var anim = el.getAnimations();
        for (var n = 0; n < anim.length; n++) {
          if (anim[n].playState === 'running') {
            durchgang.bewegt = true;
            break;
          }
        }
      }
    }
    nachsuchen(liste, x, y);
    var bloecke = [];
    for (var j = 0; j < liste.length; j++) bloecke.push({ el: liste[j], ops: ebenen(liste[j]).haupt });
    // ::after über die Kinder heben
    for (var i = 0; i < liste.length; i++) {
      var nach = ebenen(liste[i]).nach;
      if (!nach.length) continue;
      var ziel = 0;
      while (ziel < bloecke.length && bloecke[ziel].el !== liste[i] && !liste[i].contains(bloecke[ziel].el)) ziel++;
      bloecke.splice(ziel, 0, { el: null, ops: nach });
    }
    ops = [];
    for (var b = 0; b < bloecke.length; b++) for (var o = 0; o < bloecke[b].ops.length; o++) ops.push(bloecke[b].ops[o]);
    // Für stapelBei: die Rechtecke der Elemente, flach als Zahlen.
    ops.kanten = new Float64Array(liste.length * 4);
    for (var e2 = 0; e2 < liste.length; e2++) {
      var re = rechteck(liste[e2]);
      ops.kanten[e2 * 4] = re.left;
      ops.kanten[e2 * 4 + 1] = re.right;
      ops.kanten[e2 * 4 + 2] = re.top;
      ops.kanten[e2 * 4 + 3] = re.bottom;
    }
    durchgang.stapel.set(schluessel, ops);
    return ops;
  }

  // Gemischter Grund an (x, y), je Durchgang gemerkt — die Spalten des
  // Glasmodells liegen auf einem festen 8px-Raster und werden von
  // benachbarten Elementen geteilt.
  function grundBei(x, y, zeilen) {
    var schluessel = x + ',' + y;
    if (durchgang.grund.has(schluessel)) return durchgang.grund.get(schluessel);
    var f = mischen(stapelBei(x, y, zeilen), x, y);
    durchgang.grund.set(schluessel, f);
    return f;
  }

  // Der Stapel, der an (x, y) am besten passt. Sonden gibt es nur auf einem
  // Raster (alle 48px, in zwei Zeilen der Leiste); unter den vier Sonden
  // ringsum zählt die, deren Elemente die Stelle am vollständigsten abdecken,
  // bei Gleichstand die nächste. So liegt eine Kante zwischen zwei Sonden —
  // die 24px schmale Fuge zwischen zwei dunklen Karten, eine Section-Grenze
  // quer durch die Leiste — dort, wo sie wirklich ist.
  function stapelBei(x, y, zeilen) {
    var W = innerWidth;
    var x0 = Math.floor(x / SONDEN_RASTER) * SONDEN_RASTER;
    var bester = null;
    var besteZahl = -1;
    var besterAbstand = Infinity;
    for (var i = 0; i < 2; i++) {
      var px = Math.min(W - 1, Math.max(1, x0 + i * SONDEN_RASTER));
      for (var j = 0; j < zeilen.length; j++) {
        var py = zeilen[j];
        var ops = stapel(px, py);
        var kn = ops.kanten;
        var zahl = 0;
        for (var k = 0; k < kn.length; k += 4) {
          if (x >= kn[k] && x < kn[k + 1] && y >= kn[k + 2] && y < kn[k + 3]) zahl++;
        }
        var abstand = Math.abs(px - x) + Math.abs(py - y);
        if (zahl > besteZahl || (zahl === besteZahl && abstand < besterAbstand)) {
          bester = ops;
          besteZahl = zahl;
          besterAbstand = abstand;
        }
      }
    }
    return bester;
  }

  function mischen(ops, x, y) {
    var r = 0, g = 0, b = 0, rest = 1;
    for (var k = 0; k < ops.length; k++) {
      var f = ebenenFarbe(ops[k], x, y);
      if (f === null) continue;
      if (f === WARTET) {
        durchgang.wartet = true;
        return null;
      }
      var a = f[3];
      if (a <= 0) continue;
      r += rest * a * f[0];
      g += rest * a * f[1];
      b += rest * a * f[2];
      rest *= 1 - a;
      if (rest < 0.01) break;
    }
    // Was durchscheint, ist Weiß (Leinwand des Browsers).
    return [r + rest * 255, g + rest * 255, b + rest * 255, 1];
  }

  // --- Die Elemente der Leiste ---------------------------------------------

  function sichtbarRechteck(el) {
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  }

  function elemente() {
    var liste = [];
    var logo = wrapper.querySelector('.header__heading-logo');
    var r = logo && sichtbarRechteck(logo);
    if (r) liste.push({ name: 'Logo', el: logo, r: r, ziel: ZIEL_GRAFIK, gruppe: 'mitte', art: 'logo' });
    var menue = wrapper.querySelectorAll(
      '.header__inline-menu > ul > li > a.header__menu-item, .header__inline-menu > ul > li > header-menu > details > summary.header__menu-item'
    );
    for (var k = 0; k < menue.length; k++) {
      var item = menue[k];
      var text = item.querySelector('span') || item;
      r = sichtbarRechteck(text);
      if (!r) continue;
      liste.push({
        name: (text.textContent || '').trim(),
        el: item,
        r: r,
        ziel: ZIEL_TEXT,
        gruppe: 'links',
        art: 'text',
        aktiv: !!item.querySelector('.header__active-menu-item')
      });
    }
    var symbole = wrapper.querySelectorAll('.header__icon');
    for (var j = 0; j < symbole.length; j++) {
      var sym = symbole[j];
      var svgs = sym.querySelectorAll('svg');
      r = null;
      for (var s = 0; s < svgs.length && !r; s++) r = sichtbarRechteck(svgs[s]);
      if (!r) continue;
      var name = sym.classList.contains('header__icon--cart') ? 'Warenkorb' : sym.classList.contains('header__icon--account') ? 'Konto' : sym.classList.contains('header__icon--menu') ? 'Menü' : sym.classList.contains('header__icon--search') ? 'Suche' : 'Symbol';
      liste.push({ name: name, el: sym, r: r, ziel: ZIEL_GRAFIK, gruppe: sym.closest('.header__icons') ? 'rechts' : 'links', art: 'symbol' });
      // Der Warenkorb-Zähler steht halb neben dem Symbol, auf eigenem Grund.
      var zaehler = sym.querySelector('.cart-count-bubble');
      var rz = zaehler && sichtbarRechteck(zaehler);
      if (rz) liste.push({ name: 'Zähler', el: sym, r: rz, ziel: ZIEL_GRAFIK, gruppe: sym.closest('.header__icons') ? 'rechts' : 'links', art: 'symbol' });
    }
    return liste;
  }

  function tinte(e, ton) {
    if (ton === 'hell') return e.art === 'logo' ? [0, 0, 0, 1] : FARBEN.tinte;
    return e.art === 'text' && !e.aktiv ? FARBEN.weissLeise : FARBEN.weiss;
  }

  // Dawn blendet die Leiste beim Herunterscrollen aus (top: -Höhe). Gemessen
  // wird dort, wo sie beim Zurückscrollen wieder erscheint.
  function versatz() {
    if (!sektion || !sektion.classList.contains('shopify-section-header-hidden')) return 0;
    var top = sektion.getBoundingClientRect().top;
    return top < 0 ? -top : 0;
  }

  function vermessen(e, dy, kopf) {
    var r = e.r;
    var top = r.top + dy;
    var H = innerHeight;
    var ky = function (y) {
      return Math.min(H - 1, Math.max(1, y));
    };
    // Sonden in zwei Zeilen der Leiste (30 und 70 % ihrer Höhe).
    var zeilen = [ky(kopf.top + dy + kopf.height * 0.3), ky(kopf.top + dy + kopf.height * 0.7)];
    unsicher = false;
    unbekannt = false;
    var f;
    // Innen: die Stellen direkt hinter dem Element (ohne Glas zählt jede).
    var spalten = Math.max(2, Math.min(6, Math.round(r.width / 14)));
    e.innen = [];
    for (var s = 0; s < spalten; s++) {
      var x = r.left + ((s + 0.5) * r.width) / spalten;
      for (var z = 0; z < 2; z++) {
        var y = top + ((z + 0.5) * r.height) / 2;
        f = mischen(stapelBei(x, y, zeilen), x, y);
        if (!f) return false;
        e.innen.push(f);
      }
    }
    // Mit Glas: Was hinter dem Element zu sehen ist, ist der Grund,
    // weichgezeichnet mit blur(20px) — einer Gaußglocke mit 20px
    // Standardabweichung —, darüber 10 % Schwarz. Nachgerechnet: der Grund
    // in Spalten alle 8px von 40px vor bis 40px hinter dem Element und in vier
    // Zeilen über die Höhe der Leiste (nur innerhalb der Leiste, denn nur dort
    // liegt das Glas); daraus gaußgewichtet der weiche Grund an Stellen alle
    // 8px über das Element, oben und unten. Es zählt die schlechteste Stelle.
    e.glas = null;
    if (FARBEN.glasAn) {
      var gy = [0.15, 0.38, 0.62, 0.85].map(function (a) {
        return Math.round(ky(kopf.top + dy + kopf.height * a));
      });
      var x0 = Math.ceil(Math.max(kopf.left + 1, r.left - 2 * GLAS_SIGMA) / GLAS_SCHRITT) * GLAS_SCHRITT;
      var x1 = Math.min(kopf.right - 1, r.right + 2 * GLAS_SIGMA);
      var gx = [];
      var grund = [];
      for (var xc = x0; xc <= x1; xc += GLAS_SCHRITT) {
        gx.push(xc);
        for (var q = 0; q < gy.length; q++) {
          f = grundBei(xc, gy[q], zeilen);
          if (!f) return false;
          grund.push(f);
        }
      }
      var zweiSigma2 = 2 * GLAS_SIGMA * GLAS_SIGMA;
      var gewicht = function (d) {
        return Math.exp(-(d * d) / zweiSigma2);
      };
      var m = Math.max(1, Math.round(r.width / GLAS_SCHRITT));
      var ye = [top + r.height * 0.25, top + r.height * 0.75];
      var wy = ye.map(function (yy) {
        return gy.map(function (g) {
          return gewicht(g - yy);
        });
      });
      var hinterGlas = gx.length
        ? []
        : e.innen.map(function (fi) {
            return ueber(FARBEN.glas, fi);
          });
      for (var i = 0; i <= m && gx.length; i++) {
        var xe = r.left + (r.width * i) / m;
        var wx = gx.map(function (g) {
          return gewicht(g - xe);
        });
        for (var t = 0; t < ye.length; t++) {
          var sw = 0;
          var acc = [0, 0, 0];
          for (var c2 = 0; c2 < gx.length; c2++) {
            for (var q2 = 0; q2 < gy.length; q2++) {
              var w = wx[c2] * wy[t][q2];
              var g2 = grund[c2 * gy.length + q2];
              sw += w;
              acc[0] += w * g2[0];
              acc[1] += w * g2[1];
              acc[2] += w * g2[2];
            }
          }
          hinterGlas.push(ueber(FARBEN.glas, [acc[0] / sw, acc[1] / sw, acc[2] / sw, 1]));
        }
      }
      e.glas = {};
    }
    e.unsicher = unsicher;
    e.unbekannt = unbekannt;
    e.frei = {};
    ['hell', 'dunkel'].forEach(function (ton) {
      var tt = tinte(e, ton);
      var schlechtester = Infinity;
      for (var k = 0; k < e.innen.length; k++) schlechtester = Math.min(schlechtester, tinteKontrast(tt, e.innen[k]));
      e.frei[ton] = schlechtester;
      if (!e.glas) return;
      var glas = Infinity;
      for (var h = 0; h < hinterGlas.length; h++) glas = Math.min(glas, tinteKontrast(tt, hinterGlas[h]));
      e.glas[ton] = glas;
    });
    return true;
  }

  // --- Entscheiden ---------------------------------------------------------

  // Merker für die Hysterese: gemeinsamer Ton der Leiste, je Gruppe, und je
  // Einheit (das DOM-Element, das die Klasse bekommt — Warenkorb-Symbol und
  // Zähler sind eine Einheit) der Ton samt Zeitpunkt des letzten Wechsels.
  var zustand = {
    kopf: { ton: null, gemeinsam: false },
    links: { ton: null, gemeinsam: false },
    mitte: { ton: null, gemeinsam: false },
    rechts: { ton: null, gemeinsam: false }
  };
  var einheitTon = new WeakMap();
  function einheitZustand(el) {
    var z = einheitTon.get(el);
    if (!z) {
      z = { ton: null, zeit: 0 };
      einheitTon.set(el, z);
    }
    return z;
  }

  // Schlechtester Wert je Ton, gemessen am Ziel (1 = Ziel erreicht). Auf
  // geschätztem Grund zählt das Ziel mit Reserve.
  function wertung(liste, feld) {
    var w = { hell: Infinity, dunkel: Infinity };
    for (var k = 0; k < liste.length; k++) {
      var ziel = liste[k].ziel * (liste[k].unsicher ? UNSICHER : 1);
      w.hell = Math.min(w.hell, liste[k][feld].hell / ziel);
      w.dunkel = Math.min(w.dunkel, liste[k][feld].dunkel / ziel);
    }
    return w;
  }

  // Der Ton, dessen schlechtester Punkt besser ist — mit Hysterese. Verfehlt
  // der jetzige sein Ziel (Wert unter `reicht`), genügt ein kleiner Vorsprung
  // des anderen; sonst muss er klar besser sein. Auf bewegtem Grund
  // (Laufband, laufendes Video) bleibt der Ton, solange er reicht: sonst
  // wechselte die Leiste mit jedem Bild.
  function waehlen(akt, w, bewegt, reicht) {
    if (!akt) return w.hell >= w.dunkel ? 'hell' : 'dunkel';
    var a = w[akt];
    var o = w[ANDERS[akt]];
    if (a < reicht && o > a * 1.05 && (o >= reicht || o > a * HYSTERESE)) return ANDERS[akt];
    if (!bewegt && o > a * HYSTERESE) return ANDERS[akt];
    return akt;
  }

  var startZeit = performance.now();
  var gestartet = false;

  function uebernehmen(el, ton, jetzt) {
    var z = einheitZustand(el);
    if (!ton || ton === z.ton) return false;
    if (z.ton && jetzt - z.zeit < MIN_VERWEIL) {
      spaeter(z.zeit + MIN_VERWEIL - jetzt + 10);
      return false;
    }
    z.ton = ton;
    z.zeit = jetzt;
    return true;
  }

  // Eine Nachmessung zu einem späteren Zeitpunkt; die früheste Anforderung gewinnt.
  var spaeterTimer = 0;
  var spaeterUm = 0;
  function spaeter(ms) {
    var um = performance.now() + Math.max(20, ms);
    if (spaeterTimer && spaeterUm <= um) return;
    clearTimeout(spaeterTimer);
    spaeterUm = um;
    spaeterTimer = setTimeout(function () {
      spaeterTimer = 0;
      planen();
    }, Math.max(20, ms));
  }

  // --- Beobachten ----------------------------------------------------------

  var sektionsBeobachter = null;
  var beobachtet = [];
  function sektionenBeobachten(neu) {
    if (!window.MutationObserver) return;
    var liste = Array.from(neu);
    if (liste.length === beobachtet.length && liste.every(function (s) { return beobachtet.indexOf(s) > -1; })) return;
    if (!sektionsBeobachter) sektionsBeobachter = new MutationObserver(planen);
    sektionsBeobachter.disconnect();
    beobachtet = liste;
    for (var k = 0; k < liste.length; k++) {
      sektionsBeobachter.observe(liste[k], {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'src', 'srcset', 'poster', 'open', 'aria-hidden']
      });
    }
  }

  // --- Messen und anwenden -------------------------------------------------

  var stand = { messungen: 0, letzteMs: 0, maxMs: 0, summeMs: 0, sonden: 0, glas: false, kopf: null, elemente: [], spitzen: [] };

  function messen() {
    if (document.hidden) return;
    if (wrapper.classList.contains('loume-header--menu-open')) return;
    var anfang = performance.now();
    farbenLesen();
    neuerDurchgang();
    var kopf = wrapper.getBoundingClientRect();
    if (!kopf.width || !kopf.height) return;
    var dy = versatz();
    var liste = elemente();
    for (var k = 0; k < liste.length; k++) {
      var e = liste[k];
      if (e.r.bottom + dy < 0 || e.r.top + dy > innerHeight) continue;
      if (!vermessen(e, dy, kopf)) e.wartet = true;
    }
    var jetzt = performance.now();
    var glasSichtbar = FARBEN.glasAn;
    var feld = glasSichtbar ? 'glas' : 'frei';
    var reicht = glasSichtbar ? GLAS_RESERVE : 1;
    var bewegt = durchgang.bewegt;
    var GRUPPEN = ['links', 'mitte', 'rechts'];
    var gruppen = { links: [], mitte: [], rechts: [] };
    var wartendeGruppe = {};
    var standard = {};
    var einheiten = new Map();
    for (var j = 0; j < liste.length; j++) {
      var ej = liste[j];
      if (ej.wartet) wartendeGruppe[ej.gruppe] = true;
      else if (ej.innen) {
        var ei = einheiten.get(ej.el);
        if (!ei) {
          ei = { el: ej.el, gruppe: ej.gruppe, liste: [] };
          einheiten.set(ej.el, ei);
          gruppen[ej.gruppe].push(ei);
        }
        ei.liste.push(ej);
        if (ej.unbekannt) standard[ej.gruppe] = true;
      }
    }
    var geaendert = false;
    var eintraege = function (einheitenListe) {
      var l = [];
      einheitenListe.forEach(function (u) {
        l = l.concat(u.liste);
      });
      return l;
    };
    // Zusammen bleibt es bis 3 % unter dem Ziel, zusammen wird es erst ab
    // 3 % darüber — so springt die Leiste an der Grenze nicht hin und her.
    var gemeinsamerTon = function (merker, liste2) {
      var w = wertung(liste2, feld);
      var t = waehlen(merker.ton, w, bewegt, reicht);
      var ok = w[t] >= reicht * (merker.gemeinsam ? 0.97 : 1.03);
      merker.gemeinsam = ok;
      if (ok) merker.ton = t;
      return ok ? t : null;
    };

    // Unbekannter Grund (langer Film ohne lesbares Bild): diese Gruppe
    // bekommt keine Klasse, es gilt loume-header.css wie ohne JavaScript.
    var bereit = GRUPPEN.filter(function (g) {
      return gruppen[g].length && !wartendeGruppe[g] && !standard[g];
    });

    // Immer EIN Ton für die ganze Leiste (Gründer, 27.09.2026: „es darf nur
    // alles weiß oder schwarz werden, nie nur ein bestimmter Teil“). Er
    // richtet sich nach dem schlechtesten Punkt ALLER Elemente. Wartet noch
    // ein Bild, bleibt alles, wie es ist; ist irgendwo der Grund unbekannt
    // (langer Film ohne lesbares Bild), gilt für die ganze Leiste der Stand
    // ohne JavaScript (loume-header.css) — der ist ebenfalls einheitlich.
    var ton = new Map();
    var wartetEtwas = Object.keys(wartendeGruppe).length > 0;
    var unbekannterGrund = Object.keys(standard).length > 0;
    var alleEinheiten = [];
    GRUPPEN.forEach(function (g) {
      alleEinheiten = alleEinheiten.concat(gruppen[g]);
    });
    if (alleEinheiten.length && !wartetEtwas && !unbekannterGrund) {
      var tAlle = waehlen(zustand.kopf.ton, wertung(eintraege(alleEinheiten), feld), bewegt, reicht);
      zustand.kopf.ton = tAlle;
      zustand.kopf.gemeinsam = true;
      alleEinheiten.forEach(function (u) {
        ton.set(u, tAlle);
      });
    } else {
      zustand.kopf.gemeinsam = false;
    }
    // Ein Wechsel gilt für alle Elemente zugleich: Die Verweilzeit zählt für
    // die Leiste als Ganzes, sonst stünde ein Element kurz im alten Ton.
    var sperreBis = 0;
    alleEinheiten.forEach(function (u) {
      var z0 = einheitZustand(u.el);
      if (z0.ton && ton.get(u) && ton.get(u) !== z0.ton) sperreBis = Math.max(sperreBis, z0.zeit + MIN_VERWEIL);
    });
    if (sperreBis > jetzt) {
      spaeter(sperreBis - jetzt + 10);
      ton.clear();
    }
    alleEinheiten.forEach(function (u) {
      var z = einheitZustand(u.el);
      if (unbekannterGrund && !wartetEtwas) {
        if (z.ton !== null) {
          z.ton = null;
          z.zeit = jetzt;
        }
        if (u.el.classList.contains(KL_EL.hell) || u.el.classList.contains(KL_EL.dunkel)) {
          u.el.classList.remove(KL_EL.hell, KL_EL.dunkel);
          geaendert = true;
        }
        return;
      }
      if (ton.has(u) && ton.get(u) !== z.ton) {
        z.ton = ton.get(u);
        z.zeit = jetzt;
        geaendert = true;
      }
      var t = z.ton;
      if (!t || u.el.classList.contains(KL_EL[t])) return;
      u.el.classList.remove(KL_EL[ANDERS[t]]);
      u.el.classList.add(KL_EL[t]);
      geaendert = true;
    });

    if (geaendert && !gestartet) {
      gestartet = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          wrapper.classList.remove('loume-kopf--start');
        });
      });
    }
    if (durchgang.wartet) spaeter(WARTEN_MAX + 30);

    // Läuft ein Video dahinter, dessen Bild lesbar ist oder das ein
    // Schlussbild hat, oder bewegt sich etwas per CSS-Animation (Laufband),
    // in Abständen nachmessen.
    if (durchgang.bewegt) spaeter(VIDEO_TAKT);
    for (var v = 0; v < durchgang.videos.length; v++) {
      var vid = durchgang.videos[v];
      if (vid.paused || vid.ended) continue;
      var vb = videoBilder.get(vid);
      if ((vb && !vb.gesperrt) || schlussbildAdresse(vid)) {
        spaeter(VIDEO_TAKT);
        break;
      }
    }
    sektionenBeobachten(durchgang.sektionen);

    var dauer = performance.now() - anfang;
    stand.messungen++;
    stand.letzteMs = dauer;
    stand.maxMs = Math.max(stand.maxMs, dauer);
    if (dauer > 4) {
      stand.spitzen.push({ ms: Math.round(dauer * 10) / 10, y: Math.round(scrollY), sonden: durchgang.sonden, nr: stand.messungen });
      if (stand.spitzen.length > 12) stand.spitzen.shift();
    }
    stand.summeMs += dauer;
    stand.sonden = durchgang.sonden;
    stand.glas = glasSichtbar;
    stand.kopf = zustand.kopf.gemeinsam ? zustand.kopf.ton : 'gemischt';
    stand.gruppen = {};
    GRUPPEN.forEach(function (g) {
      stand.gruppen[g] = standard[g] ? 'standard' : zustand.kopf.gemeinsam ? 'leiste' : zustand[g].gemeinsam ? 'gruppe' : 'einzeln';
    });
    stand.elemente = liste.map(function (e) {
      return {
        name: e.name,
        gruppe: e.gruppe,
        ton: einheitZustand(e.el).ton,
        wartet: !!e.wartet,
        unsicher: !!e.unsicher,
        unbekannt: !!e.unbekannt,
        frei: e.frei,
        glas: e.glas,
        ziel: e.ziel
      };
    });
    durchgang = null;
  }

  var geplant = false;
  var letzte = 0;
  function lauf() {
    geplant = false;
    letzte = performance.now();
    try {
      messen();
    } catch (e) {
      durchgang = null;
      if (window.console) console.warn('loume-kopf-kontrast:', e);
    }
  }
  function planen() {
    if (geplant) return;
    geplant = true;
    var warte = MIN_ABSTAND - (performance.now() - letzte);
    if (warte > 0) {
      setTimeout(function () {
        requestAnimationFrame(lauf);
      }, warte);
    } else {
      requestAnimationFrame(lauf);
    }
  }

  // --- Handy-Menü: Schrift und Glas nach dem Grund --------------------------
  //
  // Gründer, 27.09.2026: Das offene Menü ist dasselbe Liquid Glass wie die
  // Leiste (10 % Schwarz), die Schrift darin überall EIN Ton. Liegt das Menü
  // teils auf dunklem, teils auf hellem Grund (Startseite: Hero oben, heller
  // Seitengrund darunter; Warenkorb: dunkler Fuß unten), reicht so kein Ton
  // überall. Der Ton ist immer der der Leiste; das Glas wird nur DORT
  // getönt, wo er es braucht — als weicher Verlauf von oben nach unten, je
  // Menütext gerade so stark, dass er 4,5 : 1 (plus 10 % Reserve) erreicht:
  // für Weiß dunkler, für Schwarz heller.
  // Gemessen wird der Grund hinter jedem Menütext, gemittelt über ±30px (grob
  // wie blur(30px)). Filme ohne lesbares Bild zählen hier als dunkel — die
  // Filme im Shop sind Nachtszenen.
  var menueDetails = wrapper.querySelector('#Details-menu-drawer-container');
  var MENUE_ZIEL = 4.5 * GLAS_RESERVE;
  var MENUE_MAX = 0.7;
  var menueTon = null;
  var menueModus = false;
  function menueGlas(b) {
    // 10 % Schwarz, darüber b Weiß — als eine Farbe zusammengefasst.
    var alpha = 0.1 + 0.9 * b;
    var c = alpha > 0 ? (b * 255) / alpha : 0;
    return [c, c, c, alpha];
  }
  function rgba(f) {
    return 'rgba(' + Math.round(f[0]) + ', ' + Math.round(f[1]) + ', ' + Math.round(f[2]) + ', ' + f[3].toFixed(3) + ')';
  }
  function menueMessen() {
    var drawer = wrapper.querySelector('.menu-drawer');
    if (!drawer || !menueDetails || !menueDetails.open) return;
    if (drawer.classList.contains('loume-blatt')) return blattMessen(drawer);
    farbenLesen();
    neuerDurchgang();
    menueModus = true;
    var oben = drawer.getBoundingClientRect().top;
    var texte = drawer.querySelectorAll(
      '.menu-drawer__menu > li > a.menu-drawer__menu-item, .loume-produktmenue-drawer__titel, .menu-drawer__utility-links a, .menu-drawer__utility-links button'
    );
    var punkte = [];
    try {
      for (var k = 0; k < texte.length; k++) {
        var r = texte[k].getBoundingClientRect();
        if (!r.width || !r.height || r.bottom < 0 || r.top > innerHeight) continue;
        var acc = [0, 0, 0];
        var n = 0;
        for (var i = 0; i < 5; i++) {
          for (var j = 0; j < 3; j++) {
            var x = Math.min(innerWidth - 1, Math.max(1, r.left - 30 + (i * (r.width + 60)) / 4));
            var y = Math.min(innerHeight - 1, Math.max(1, r.top - 30 + (j * (r.height + 60)) / 2));
            var f = mischen(stapel(x, y), x, y);
            if (!f) continue;
            acc[0] += f[0];
            acc[1] += f[1];
            acc[2] += f[2];
            n++;
          }
        }
        if (n) punkte.push({ y: r.top + r.height / 2 - oben, g: [acc[0] / n, acc[1] / n, acc[2] / n, 1] });
      }
    } finally {
      menueModus = false;
    }
    var wartet = durchgang.wartet;
    durchgang = null;
    if (wartet) spaeterMenue(WARTEN_MAX + 30);
    if (!punkte.length) return;
    punkte.sort(function (p1, p2) {
      return p1.y - p2.y;
    });
    // Je Punkt die kleinste Tönung, die reicht (Schritte von 0,025).
    var maxWeiss = 0;
    var maxSchwarz = 0;
    punkte.forEach(function (pt) {
      var s;
      pt.a = MENUE_MAX;
      for (s = 0.1; s <= MENUE_MAX + 1e-6; s += 0.025) {
        if (tinteKontrast(FARBEN.weiss, ueber([0, 0, 0, s], pt.g)) >= MENUE_ZIEL) {
          pt.a = s;
          break;
        }
      }
      pt.b = MENUE_MAX;
      for (s = 0; s <= MENUE_MAX + 1e-6; s += 0.025) {
        if (tinteKontrast(FARBEN.tinte, ueber(menueGlas(s), pt.g)) >= MENUE_ZIEL) {
          pt.b = s;
          break;
        }
      }
      maxWeiss = Math.max(maxWeiss, pt.a - 0.1);
      maxSchwarz = Math.max(maxSchwarz, pt.b);
    });
    // Der Ton ist der der Leiste — Leiste und Menü sind immer beide weiß
    // oder beide schwarz (Gründer: „nie nur ein bestimmter Teil“). Die
    // Tönung sorgt nur noch dafür, dass er überall trägt.
    var logo = wrapper.querySelector('.header__heading-logo');
    var ton = logo && logo.classList.contains(KL_EL.hell) ? 'hell'
      : logo && logo.classList.contains(KL_EL.dunkel) ? 'dunkel'
      : document.body.classList.contains('loume-kopf-dunkel') ? 'hell' : 'dunkel';
    menueTon = ton;
    var farben = punkte.map(function (pt) {
      return { y: pt.y, f: ton === 'dunkel' ? [0, 0, 0, pt.a] : menueGlas(pt.b) };
    });
    var st = wrapper.style;
    var gleich = farben.every(function (x) {
      return rgba(x.f) === rgba(farben[0].f);
    });
    if (gleich) {
      st.setProperty('--loume-kopf-glas-menue', rgba(farben[0].f));
      st.setProperty('--loume-kopf-glas-menue-verlauf', 'none');
    } else {
      var stopps = [rgba(farben[0].f) + ' 0px'];
      farben.forEach(function (x) {
        stopps.push(rgba(x.f) + ' ' + Math.max(0, Math.round(x.y)) + 'px');
      });
      stopps.push(rgba(farben[farben.length - 1].f) + ' 100%');
      st.setProperty('--loume-kopf-glas-menue', 'transparent');
      st.setProperty('--loume-kopf-glas-menue-verlauf', 'linear-gradient(to bottom, ' + stopps.join(', ') + ')');
    }
    st.setProperty('--lo-menue-tinte', ton === 'dunkel' ? '#ffffff' : '#121212');
    st.setProperty('--lo-menue-tinte-leise', ton === 'dunkel' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(18, 18, 18, 0.6)');
    stand.menue = {
      ton: ton,
      staerksteToenung: ton === 'dunkel' ? maxWeiss + 0.1 : maxSchwarz,
      punkte: farben.map(function (x) {
        return Math.round(x.y) + ':' + rgba(x.f);
      })
    };
  }
  // --- Handy-Menü „Glasblatt“: die Seite dahinter dimmen -------------------
  //
  // Entwurf A (Gründer, 28.09.2026; snippets/loume-menue-blatt.liquid,
  // assets/loume-menue-blatt.css). Das Blatt bleibt dasselbe 10-%-Glas wie
  // die Leiste, die Schrift darauf ist immer weiß. Lesbar wird sie nicht
  // durch dunkleres Glas, sondern weil die Seite dahinter nachtblau gedimmt
  // wird — nur so weit wie nötig. Gemessen wird hinter dem Teil des Blatts,
  // der Schrift trägt (ab den Zeilen bis unten; lässt sich das Blatt rollen,
  // das ganze Blatt): ein Raster alle 40px, jeder Punkt gemittelt mit seinen
  // Nachbarn (grob wie blur(28px)). Der hellste Punkt bestimmt den kleinsten
  // Dimmwert, bei dem Weiß auf „Seite gedimmt + 10 % Schwarz“ 5,2 : 1 hat
  // (4,5 plus Reserve für Glanz, Lichtkante und Sättigung). Hinter einem
  // Film ohne lesbares Bild zählt sein Poster, dann mit 15 % mehr Reserve.
  // Ergebnis: --lo-menue-dimmen am Wrapper (Deckkraft des Dimmers, ohne
  // Skript 0,52). Die Leiste bleibt unberührt: der Dimmer spart ihr
  // Rechteck aus, ihr Ton wird bei offenem Menü nicht neu bestimmt.
  var BLATT_ZIEL = 5.2;
  var BLATT_UNSICHER = 1.15;
  var BLATT_MIN = 0.18;
  var BLATT_MAX = 0.62;
  var BLATT_RASTER = 40;
  var BLATT_DIMMFARBE = [12, 17, 38, 1];
  var BLATT_GLAS = [0, 0, 0, 0.1];
  var blattModus = false;
  function blattMessen(blatt) {
    farbenLesen();
    neuerDurchgang();
    menueModus = true;
    blattModus = true;
    var br = blatt.getBoundingClientRect();
    var rollbar = blatt.querySelector('.loume-blatt__rollbar');
    var liste = blatt.querySelector('.loume-blatt__liste');
    var oben = br.top;
    if (liste && rollbar && rollbar.scrollHeight <= rollbar.clientHeight + 1) oben = liste.getBoundingClientRect().top;
    var unten = Math.min(br.bottom, innerHeight);
    var knoten = [];
    var spalten = 0;
    var hellster = null;
    var hellsteLum = -1;
    var unsicherBlatt = false;
    try {
      var xs = [];
      for (var x = br.left - BLATT_RASTER; x <= br.right + BLATT_RASTER; x += BLATT_RASTER) xs.push(Math.min(innerWidth - 1, Math.max(1, x)));
      var ys = [];
      for (var y = oben - BLATT_RASTER; y <= unten + BLATT_RASTER; y += BLATT_RASTER) ys.push(Math.min(innerHeight - 1, Math.max(1, y)));
      spalten = xs.length;
      for (var j = 0; j < ys.length; j++) {
        for (var i = 0; i < xs.length; i++) {
          unsicher = false;
          var f = mischen(stapel(xs[i], ys[j]), xs[i], ys[j]);
          if (f && unsicher) unsicherBlatt = true;
          knoten.push(f);
        }
      }
      // Innere Knoten: Mittel mit den acht Nachbarn, hellsten merken.
      for (var jj = 1; jj < ys.length - 1; jj++) {
        for (var ii = 1; ii < xs.length - 1; ii++) {
          var acc = [0, 0, 0];
          var n = 0;
          for (var dy = -1; dy <= 1; dy++) {
            for (var dx = -1; dx <= 1; dx++) {
              var k = knoten[(jj + dy) * spalten + ii + dx];
              if (!k) continue;
              acc[0] += k[0];
              acc[1] += k[1];
              acc[2] += k[2];
              n++;
            }
          }
          if (!n) continue;
          var mittel = [acc[0] / n, acc[1] / n, acc[2] / n, 1];
          var l = lum(mittel);
          if (l > hellsteLum) {
            hellsteLum = l;
            hellster = mittel;
          }
        }
      }
    } finally {
      menueModus = false;
      blattModus = false;
    }
    var wartet = durchgang.wartet;
    durchgang = null;
    if (wartet) spaeterMenue(WARTEN_MAX + 30);
    if (!hellster) return;
    var ziel = BLATT_ZIEL * (unsicherBlatt ? BLATT_UNSICHER : 1);
    var s = BLATT_MIN;
    // Das Blatt hat immer das dunkle Glas (auf hellen Seiten ist die Leiste
    // ungescrollt weiß — --loume-kopf-glas gilt hier nicht).
    while (s < BLATT_MAX && tinteKontrast(FARBEN.weiss, ueber(BLATT_GLAS, ueber([BLATT_DIMMFARBE[0], BLATT_DIMMFARBE[1], BLATT_DIMMFARBE[2], s], hellster))) < ziel) s += 0.01;
    s = Math.min(BLATT_MAX, s);
    wrapper.style.setProperty('--lo-menue-dimmen', s.toFixed(2));
    stand.menue = {
      art: 'blatt',
      dimmen: +s.toFixed(2),
      hellster: hellster.slice(0, 3).map(Math.round),
      ziel: +ziel.toFixed(2),
      unsicher: unsicherBlatt,
      wartet: !!wartet,
      bereich: [Math.round(br.left), Math.round(oben), Math.round(br.right), Math.round(unten)],
      punkte: knoten.length
    };
  }

  var menueTimer = 0;
  function spaeterMenue(ms) {
    clearTimeout(menueTimer);
    menueTimer = setTimeout(function () {
      try {
        menueMessen();
      } catch (e) {
        durchgang = null;
      }
    }, ms);
  }
  if (menueDetails) {
    menueDetails.addEventListener('toggle', function () {
      if (!menueDetails.open) return;
      try {
        menueMessen();
      } catch (e) {
        durchgang = null;
      }
      // Glasblatt: noch einmal, wenn es ausgerollt ist (560 ms).
      spaeterMenue(wrapper.querySelector('.menu-drawer.loume-blatt') ? 650 : 400);
    });
    // Drehen oder Safari-Leisten: offen neu messen.
    window.addEventListener('resize', function () {
      if (menueDetails.open) spaeterMenue(200);
    }, { passive: true });
  }

  // --- Anlässe ---------------------------------------------------------------

  function ausserhalbKopf(ev) {
    var t = ev.target;
    if (!t || t.nodeType !== 1 || !wrapper.contains(t)) planen();
  }

  var auf = { capture: true, passive: true };
  document.addEventListener('scroll', planen, auf); // Seite, Galerie, Karussells
  window.addEventListener('resize', function () {
    farbenLesen();
    planen();
  }, { passive: true });
  window.addEventListener('pageshow', planen);
  document.addEventListener('visibilitychange', planen);
  document.addEventListener('load', planen, true); // Bilder, Videos
  ['loadeddata', 'play', 'playing', 'pause', 'ended', 'seeked'].forEach(function (typ) {
    document.addEventListener(typ, planen, true);
  });
  document.addEventListener('transitionend', ausserhalbKopf, true);
  document.addEventListener('animationend', ausserhalbKopf, true);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(planen);

  // Zustände der Leiste (Glas, Menü, Scroll) — die eigenen Klassen zählen nicht.
  if (window.MutationObserver) {
    var kopfSchluessel = '';
    new MutationObserver(function () {
      var s = wrapper.className.replace(/\s*loume-kopf-[\w-]+/g, '');
      if (s === kopfSchluessel) return;
      kopfSchluessel = s;
      planen();
    }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  }

  // Mini-Fassungen der Bilder, die sich der Leiste nähern, vorab holen —
  // erst ab dem ersten Scrollen, damit beim Laden nichts dazukommt.
  if (window.IntersectionObserver) {
    var vorab = null;
    var vorabStart = function () {
      document.removeEventListener('scroll', vorabStart, auf);
      vorab = new IntersectionObserver(function (eintraege) {
        eintraege.forEach(function (eintrag) {
          if (!eintrag.isIntersecting) return;
          var el = eintrag.target;
          vorab.unobserve(el);
          if (el.tagName === 'VIDEO') {
            if (el.poster) mini(el.poster, false);
          } else if (el.complete && el.naturalWidth) {
            mini(el.currentSrc || el.src, el.naturalWidth * el.naturalHeight <= 160000);
          }
        });
      }, { rootMargin: '0px 0px 25% 0px' });
      document.querySelectorAll('main img, main video[poster]').forEach(function (el) {
        vorab.observe(el);
      });
    };
    document.addEventListener('scroll', vorabStart, auf);
  }

  // Kein sichtbares Umfärben beim ersten Anwenden; spätestens nach 2 s gilt
  // der sanfte Übergang.
  wrapper.classList.add('loume-kopf--start');
  setTimeout(function () {
    wrapper.classList.remove('loume-kopf--start');
  }, 2000);

  farbenLesen();
  planen();

  // Für Messwerkzeuge (tools/visual-diff/kopf-kontrast-check.mjs).
  window.LoumeKopfKontrast = {
    messen: function () {
      letzte = 0;
      lauf();
      return this.stand();
    },
    stand: function () {
      return {
        messungen: stand.messungen,
        letzteMs: stand.letzteMs,
        maxMs: stand.maxMs,
        mittelMs: stand.messungen ? stand.summeMs / stand.messungen : 0,
        sonden: stand.sonden,
        glas: stand.glas,
        kopf: stand.kopf,
        gruppen: stand.gruppen,
        elemente: stand.elemente,
        spitzen: stand.spitzen,
        menue: stand.menue,
        startMs: performance.now() - startZeit
      };
    }
  };
})();
